// Price Admin WebSocket Hook - Provides centralized WebSocket management for price-admin-api
// This connects to the price dashboard admin functionality

import { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import WebSocketDataManager from '../services/WebSocketDataManager';

const PriceAdminWebSocketContext = createContext();

export const PriceAdminWebSocketProvider = ({ children, socketUrl }) => {
  const [socket, setSocket] = useState(null);
  const [dataManager, setDataManager] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [initializationError, setInitializationError] = useState(null);

  useEffect(() => {
    if (!socketUrl) {
      console.warn(
        'PriceAdmin WebSocket URL not provided, skipping WebSocket connection',
      );
      setConnectionStatus('disabled');
      return;
    }

    let newSocket = null;

    try {
      console.log('Connecting to PriceAdmin WebSocket:', socketUrl);

      // Connect directly to the general gateway (no namespace needed)
      let fullSocketUrl = socketUrl;

      console.log('PriceAdmin WebSocket URL:', fullSocketUrl);

      // Initialize socket connection
      newSocket = io(fullSocketUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000,
      });

      // Connection event handlers
      newSocket.on('connect', () => {
        console.log('PriceAdmin WebSocket connected successfully');
        setConnectionStatus('connected');
        setInitializationError(null);
      });

      newSocket.on('disconnect', (reason) => {
        console.log('PriceAdmin WebSocket disconnected:', reason);
        setConnectionStatus('disconnected');
      });

      newSocket.on('connect_error', (error) => {
        console.error('PriceAdmin WebSocket connection error:', error);
        setConnectionStatus('error');
        setInitializationError(error.message || 'Connection failed');
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        console.log(
          `PriceAdmin WebSocket reconnection attempt ${attemptNumber}`,
        );
        setConnectionStatus('reconnecting');
      });

      newSocket.on('reconnect_failed', () => {
        console.error(
          'PriceAdmin WebSocket reconnection failed after all attempts',
        );
        setConnectionStatus('failed');
      });

      // Initialize data manager
      const manager = new WebSocketDataManager(newSocket);

      setSocket(newSocket);
      setDataManager(manager);

      // Cleanup on unmount
      return () => {
        try {
          if (newSocket) {
            newSocket.removeAllListeners();
            newSocket.disconnect();
          }
        } catch (error) {
          console.error('Error during PriceAdmin WebSocket cleanup:', error);
        }
      };
    } catch (error) {
      console.error('Failed to initialize PriceAdmin WebSocket:', error);
      setConnectionStatus('failed');
      setInitializationError(error.message || 'Initialization failed');

      // Cleanup if socket was partially created
      if (newSocket) {
        try {
          newSocket.disconnect();
        } catch (cleanupError) {
          console.error(
            'Error during failed PriceAdmin socket cleanup:',
            cleanupError,
          );
        }
      }
    }
  }, [socketUrl]);

  const value = {
    socket,
    dataManager,
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    isConnecting:
      connectionStatus === 'connecting' || connectionStatus === 'reconnecting',
    hasError: connectionStatus === 'error' || connectionStatus === 'failed',
    errorMessage: initializationError,
  };

  return (
    <PriceAdminWebSocketContext.Provider value={value}>
      {children}
    </PriceAdminWebSocketContext.Provider>
  );
};

export const usePriceAdminWebSocket = () => {
  const context = useContext(PriceAdminWebSocketContext);
  if (!context) {
    throw new Error(
      'usePriceAdminWebSocket must be used within a PriceAdminWebSocketProvider',
    );
  }
  return context;
};

export default { PriceAdminWebSocketProvider, usePriceAdminWebSocket };
